import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { containerVariants, itemVariants } from '../../lib/animations';
import { useAuth } from '../../contexts/AuthContext';
import { useHtmlBackground } from '../../hooks/useHtmlBackground';
import { api } from '../../services/api';
import { ApiError } from '../../services/apiError';
import {
  CONTAINER_MAX_WIDTH,
  HORIZONTAL_PADDING,
  TYPOGRAPHY,
  BUTTON_HEIGHT,
  SPACING,
  EMAIL_ILLUSTRATION,
  EMAIL_SPACING,
  EMAIL_TYPOGRAPHY,
} from './constants';

import playMomentsLogo from '../../../assets/icons/home-v2/header-playmoments.svg';
import cloudGroup from '../../../assets/login-email-screen/cloud-group.svg';
import backgroundDecor from '../../../assets/login-email-screen/background-decor.svg';
import leftDecor from '../../../assets/login-email-screen/left-decor.svg';
import upperRightDecor from '../../../assets/login-email-screen/upper-right-decor.svg';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ERROR_MESSAGES: Record<string, string> = {
  EMAIL_RATE_LIMIT: 'We allow 15 emails per hour, please try after sometime.',
  EMAIL_SEND_FAILED: 'Something went wrong sending your email. Please try again in sometime.',
};

const DEFAULT_ERROR = 'Something went wrong, please try again later.';
const SUPPORT_MESSAGE = 'Contact support at ioana@playmoments.app';

function getErrorMessage(error: unknown): { message: string; showSupport: boolean } {
  if (error instanceof ApiError && error.code) {
    return {
      message: ERROR_MESSAGES[error.code] || DEFAULT_ERROR,
      showSupport: true,
    };
  }
  if (error instanceof ApiError && error.status === 400) {
    return { message: error.message, showSupport: false };
  }
  return { message: DEFAULT_ERROR, showSupport: true };
}

export default function LoginEmailPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  useHtmlBackground('#DBF7F8');

  if (!authLoading && isAuthenticated) {
    navigate('/home', { replace: true });
    return null;
  }

  if (authLoading) {
    return null;
  }

  const validateEmail = (): boolean => {
    const trimmed = email.trim();
    if (!trimmed) {
      setEmailError('Please enter your email');
      return false;
    }
    if (!EMAIL_REGEX.test(trimmed)) {
      setEmailError('Please enter a valid email address');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validateEmail()) return;

    setIsLoading(true);
    setEmailError('');

    try {
      await api.requestMagicLink({ email: email.trim() });
      navigate('/login/email-sent', { state: { email: email.trim() } });
    } catch (error) {
      const { message, showSupport } = getErrorMessage(error);
      setEmailError(showSupport ? `${message} ${SUPPORT_MESSAGE}` : message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col items-center overflow-hidden"
      style={{ backgroundColor: '#DBF7F8' }}
    >
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(to bottom, #01A7C8 0%, rgba(1,167,200,0) 500px)',
        }}
      />

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className={`relative z-10 flex flex-col items-center 
          w-full ${CONTAINER_MAX_WIDTH} h-full`}
      >
        <motion.div variants={itemVariants} className={`${SPACING.logoTop} self-start ${HORIZONTAL_PADDING}`}>
          <img
            src={playMomentsLogo}
            alt="Play Moments"
            className={`${SPACING.logoWidth} h-auto`}
          />
        </motion.div>

        <motion.div
          variants={itemVariants}
          className={`relative w-full ${EMAIL_SPACING.illustrationTop} ${EMAIL_ILLUSTRATION.containerHeight} flex items-center justify-center ${HORIZONTAL_PADDING}`}
        >
          <img
            src={leftDecor}
            alt=""
            className={`absolute  
              ${EMAIL_ILLUSTRATION.leftDecorTop} 
              ${EMAIL_ILLUSTRATION.leftDecorWidth}
               ${EMAIL_ILLUSTRATION.horizontalInsetLeft}`}
          />
          <img
            src={upperRightDecor}
            alt=""
            className={`absolute 
              ${EMAIL_ILLUSTRATION.upperRightDecorTop} 
              ${EMAIL_ILLUSTRATION.upperRightDecorWidth}
              ${EMAIL_ILLUSTRATION.upperRightDecorRight} `}
          />

          <img
            src={backgroundDecor}
            alt=""
            className="absolute left-1/2 -translate-x-1/2 w-screen h-auto"
          />
          <img
            src={cloudGroup}
            alt="Cloud character with pencil"
            className={`relative ${EMAIL_ILLUSTRATION.cloudGroupWidth} h-auto`}
          />
        </motion.div>

        <motion.div variants={itemVariants} className={`w-full mt-8 flex flex-col ${EMAIL_SPACING.formGap} ${HORIZONTAL_PADDING}`}>
          <h1
            className={`font-bold ${TYPOGRAPHY.title} leading-tight tracking-[-0.04em]`}
            style={{ color: '#1B2831' }}
          >
            Log in with Email
          </h1>

          <p
            className={`${TYPOGRAPHY.description} leading-relaxed`}
            style={{ color: '#1B2831' }}
          >
            Enter your email and we&apos;ll send you a secure link to sign in – no password needed.
          </p>

          <form onSubmit={handleSubmit} className={`flex flex-col ${EMAIL_SPACING.formGap}`}>
            <div>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailError) setEmailError('');
                }}
                className={`w-full ${EMAIL_SPACING.inputHeight} px-4 rounded-xl bg-transparent ${EMAIL_TYPOGRAPHY.input} transition-colors focus:outline-none focus:ring-2 focus:ring-[#01A7C8] ${emailError ? 'border-2 border-red-500' : 'border border-[#A9B7BE]'
                  }`}
                style={{ color: '#1B2831', fontFamily: 'DM Sans' }}
                placeholder="you@example.com"
                disabled={isLoading}
                autoFocus
                autoComplete="email"
              />
              {emailError && (
                <p className={`mt-2 ${EMAIL_TYPOGRAPHY.error} text-red-600`}>{emailError}</p>
              )}
            </div>

            <p className={`${EMAIL_TYPOGRAPHY.helper} leading-relaxed`} style={{ color: '#1B2831' }}>
              New here? We&apos;ll create your account automatically.
              <br />
              Been here before? You&apos;ll pick up right where you left off.
            </p>

            <button
              type="submit"
              disabled={isLoading}
              className={`w-full ${BUTTON_HEIGHT} rounded-xl uppercase disabled:opacity-70 ${TYPOGRAPHY.button} font-black tracking-[0.72px]`}
              style={{ backgroundColor: '#1B2831', color: '#FFFFFF' }}
            >
              {isLoading ? 'SENDING...' : 'SEND ME A SIGN-IN LINK'}
            </button>
          </form>
        </motion.div>

        <div className={SPACING.buttonsBottom} />
      </motion.div>
    </div>
  );
}
